import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import { useAccount } from 'wagmi';
import {
	Button,
	Card,
	Chip,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
} from '@mui/material';
import { CreateAgentButton } from 'pages/CreateAgent/CreateAgentButton';
import { allTrades, getAgentsByAddresses, TradesType } from 'apis';
import { NAVBAR } from 'data/navbar';
import { NavLink } from 'react-router-dom';
import { SOCIAL } from 'data/socials';
import { MOBILE_BREAK_POINTS } from 'data/constant';
import Cathena from '../../assets/images/cathena.png';
import moment from 'moment';
import RefreshIcon from '@mui/icons-material/Refresh';

export type DisconnectPanelProps = {
	onDisconnect: () => void;
};

const FONT_STYLES = {
	fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
	fontSize: '14px',
	fontWeight: 500,
	letterSpacing: '0.2px',
} as const;

const Column = styled('div')`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const MobileWrapper = styled('div')`
	display: none;
	flex-direction: column;
	width: 100%;

	@media (max-width: ${MOBILE_BREAK_POINTS}px) {
		display: flex;
	}
`;

const SocialIcon = styled('img')`
	width: 24px;
	height: 24px;
	display: block;
`;

const SocialButton = styled('a')`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px; /* WCAG min touch target */
	height: 44px;
`;

const Page = styled(Box)(({ theme }) => ({
	minHeight: '100vh',
	width: '100%',
	flexDirection: 'column',
	backgroundColor: theme.palette.common.white,
	color: theme.palette.text.primary,
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
}));

const TopBar = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: theme.spacing(2.5, 2.5, 2),
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(18),
	fontWeight: 600,
	letterSpacing: '-0.01em',
}));

const AccountRow = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: theme.spacing(0, 2.5, 2.5),
}));

const Identity = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
}));

const Badge = styled(Avatar)(({ theme }) => ({
	height: 40,
	width: 40,
	borderRadius: 999,
	backgroundColor: theme.palette.primary.main,
	color: theme.palette.common.white,
	fontSize: theme.typography.pxToRem(16),
	fontWeight: 600,
}));

const Name = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(17),
	fontWeight: 500,
	lineHeight: 1.15,
}));

const Address = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(14),
	color: theme.palette.text.secondary,
	marginTop: 2,
}));

const EmptyState = styled(Box)(({ theme }) => ({
	padding: theme.spacing(5, 2.5, 6),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	textAlign: 'center',
	gap: theme.spacing(1.5),
}));

const IconCircle = styled(Box)(({ theme }) => ({
	height: 64,
	width: 64,
	borderRadius: 999,
	backgroundColor: theme.palette.grey[100],
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: theme.palette.grey[600],
}));

const EmptyTitle = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(20),
	fontWeight: 600,
}));

const EmptyHint = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(16),
	color: theme.palette.text.secondary,
	maxWidth: 320,
}));

const ConnectButton = styled(Button)(({ theme }) => ({
	marginTop: theme.spacing(2),
	backgroundColor: '#1A73E8',
	color: '#fff',
	width: '100%',
	borderRadius: theme.shape.borderRadius * 2,
	textTransform: 'none',
	fontWeight: 600,
	fontSize: 14,
	padding: '12px 20px',
	'&:hover': {
		backgroundColor: '#1558b0',
	},
}));

const TradeCard: React.FC<TradesType> = (trade) => {
	const isLong = trade.side === 'buy';
	const pnlPositive = trade.pnl >= 0;

	return (
		<div
			style={{
				padding: '2px 20px 0 20px',
				width: '100%',
			}}
		>
			<Card
				variant="outlined"
				sx={{
					//mb: 0.5,
					borderRadius: 2,
					width: '100%',
					padding: '5px 10px',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					//mb={2}
				>
					<Box display="flex" alignItems="center" gap={0.5}>
						<Typography variant="body2" fontWeight="bold">
							{trade.asset}
						</Typography>
						<Chip
							label={isLong ? 'long' : 'short'}
							size="small"
							sx={{
								backgroundColor: isLong ? '#68be72' : '#ee898b',
								fontWeight: 700,
								fontSize: 10,
								color: '#fff',
								padding: '1px 2px',
								textTransform: 'capitalize',
							}}
						/>
					</Box>
				</Box>
				<Box height={10} />
				<Typography
					color="text.secondary"
					variant={'caption'}
					display={'flex'}
				>
					Size:{' '}
					<Typography
						component="span"
						color="text.primary"
						variant={'caption'}
					>
						{trade.size}
					</Typography>
				</Typography>
				<Box height={5} />
				<Typography
					color="text.secondary"
					variant={'caption'}
					display={'flex'}
				>
					Entry:{' '}
					<Typography
						component="span"
						color="text.primary"
						variant={'caption'}
					>
						${trade.price?.toLocaleString()}
					</Typography>
				</Typography>
				<Box height={5} />
				{trade.pnl !== null &&
					!Object.is(trade.pnl, -0) &&
					trade.pnl !== 0 && (
						<Typography
							color="text.secondary"
							variant={'caption'}
							display={'flex'}
						>
							P&amp;L:{' '}
							<Typography
								component="span"
								variant={'caption'}
								sx={{
									color: pnlPositive
										? 'success.main'
										: 'error.main',
									fontWeight: 500,
								}}
							>
								{trade.pnl ? (pnlPositive ? '+' : '') : ''}
								{trade.pnl
									? '$' + trade.pnl?.toLocaleString()
									: '-'}
							</Typography>
						</Typography>
					)}
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						width: '100%',
						alignItems: 'end',
					}}
				>
					<Typography
						color="text.secondary"
						variant={'caption'}
						display={'flex'}
					>
						<Typography
							component="span"
							variant={'caption'}
							sx={{
								color: '#aaa',
								fontSize: 12,
								fontWeight: 500,
							}}
						>
							{moment(trade.createdAt).format('DD-MMM-YYYY')}
						</Typography>
					</Typography>
				</div>
			</Card>
		</div>
	);
};

export const DisconnectPanel: React.ComponentType<DisconnectPanelProps> = ({
	onDisconnect,
}) => {
	const [loading, setLoading] = useState(true);
	const [trades, setTrades] = useState<TradesType[]>([]);
	const [showButton, setShowButton] = useState(true);
	const [itemsToShow, setItemsToShow] = useState(3);
	const { address } = useAccount();

	const fetchAgentCondition = useCallback(async () => {
		try {
			if (!address) {
				setShowButton(false);
				return;
			}

			const res = await getAgentsByAddresses([address]);
			const temp = res.find((d: any) => d.address === address);
			setShowButton(!temp);
		} catch (e) {
			//
		}
	}, [address]);

	const fetchAllTrades = useCallback(async () => {
		try {
			setLoading(true);

			const res = await allTrades(address as string);
			setTrades(res);
		} catch (e) {
			//
		} finally {
			setLoading(false);
		}
	}, [address]);

	useEffect(() => {
		const updateItemsToShow = () => {
			if (window.innerHeight <= 1000) {
				setItemsToShow(3);
			} else {
				setItemsToShow(5);
			}
		};

		updateItemsToShow(); // Run once on mount
		window.addEventListener('resize', updateItemsToShow);

		return () => {
			window.removeEventListener('resize', updateItemsToShow);
		};
	}, [setItemsToShow]);

	useEffect(() => {
		fetchAllTrades();
	}, [fetchAllTrades]);

	useEffect(() => {
		fetchAgentCondition();
	}, [fetchAgentCondition]);

	return (
		<Page>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					padding: 0,
					width: '100%',
					border: '0px solid #000',
				}}
			>
				<TopBar>
					<Title>Trading Dashboard</Title>
					<ChevronRightRounded fontSize="small" color="action" />
				</TopBar>
				<Box height={20} />
				<AccountRow>
					<Identity>
						<Badge>{'HY'}</Badge>
						<Box>
							<Name>{'Anon'}</Name>
							<Address>
								{address?.slice(0, 7)}...
								{address?.slice(
									address.length - 7,
									address.length,
								)}
							</Address>
						</Box>
					</Identity>

					<AccountBalanceWalletOutlined fontSize="small" />
				</AccountRow>

				<AccountRow>
					<ConnectButton size="large" onClick={onDisconnect}>
						Disconnect
					</ConnectButton>
				</AccountRow>

				<Divider />
			</div>
			{!loading && trades?.length > 0 && (
				<div
					style={{
						display: 'flex',
						flex: 1,
						flexDirection: 'column',
						width: '100%',
						justifyContent: 'start',
					}}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							padding: '10px 20px 0 20px',
						}}
					>
						<Typography
							component="span"
							variant={'body2'}
							sx={{
								fontWeight: 600,
							}}
						>
							Trades
						</Typography>
						<RefreshIcon
							onClick={() => fetchAllTrades()}
							sx={{ color: '#444', cursor: 'pointer' }}
						/>
					</div>
					{trades?.slice(0, itemsToShow).map((trade) => (
						<>
							<Box height={10} />
							<TradeCard key={trade.id} {...trade} />
						</>
					))}
				</div>
			)}

			{!loading && trades?.length === 0 && (
				<EmptyState>
					{!showButton && (
						<>
							<IconCircle>
								<TrendingUpRounded fontSize="medium" />
							</IconCircle>
							<EmptyTitle>No activity yet</EmptyTitle>
							<EmptyHint>
								Your positions, portfolio, and history will
								appear here
							</EmptyHint>
						</>
					)}
					{showButton && (
						<>
							<IconCircle>
								<img
									src={Cathena}
									width={35}
									height={35}
									alt={'Cathena'}
								/>
							</IconCircle>
							<EmptyTitle>Authorize Agent</EmptyTitle>
							<EmptyHint style={{ fontSize: 12 }}>
								Authorize agent to start trading in Hyperliquid
							</EmptyHint>
							<CreateAgentButton
								showButton={showButton}
								fetchAgentCondition={fetchAgentCondition}
							/>
						</>
					)}
				</EmptyState>
			)}
			<div style={{ width: '100%' }}>
				<MobileWrapper>
					<nav aria-label="Primary">
						<List sx={{ py: 0 }}>
							{NAVBAR.map((d) => (
								<ListItem key={d.to} disablePadding>
									{d.target ? (
										<ListItemButton
											component="a"
											href={d.to}
											target={d.target}
											//onClick={handleClose}
										>
											<ListItemText
												primaryTypographyProps={{
													...FONT_STYLES,
												}}
												primary={d.name}
											/>
										</ListItemButton>
									) : (
										<ListItemButton
											component={NavLink as any}
											to={d.to}
											//onClick={handleClose}
										>
											<ListItemText
												primaryTypographyProps={{
													...FONT_STYLES,
												}}
												primary={d.name}
											/>
										</ListItemButton>
									)}
								</ListItem>
							))}
						</List>
					</nav>
					<Column>
						<Divider
							style={{
								color: '#8a8c92',
								backgroundColor: '#8a8c92',
							}}
						/>

						<Box
							component="nav"
							aria-label="Social"
							sx={{
								display: 'flex',
								alignItems: 'center',
								px: 2,
								py: 1.5,
								gap: 1,
							}}
						>
							{SOCIAL.map((social) => (
								<SocialButton
									key={social.name}
									aria-label={social.name}
									href={social.url}
									target="_blank"
									rel="noopener noreferrer"
								>
									<SocialIcon
										src={social.icon}
										alt=""
										loading="lazy"
									/>
								</SocialButton>
							))}
						</Box>
					</Column>
				</MobileWrapper>
			</div>
		</Page>
	);
};
